// src/PendenciaCadastro.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from './api';
import { recarregar } from './dados';
import { BuscaSelect, abrirWhatsApp } from './ui';

// Texto para comparação: minúsculo e sem acentos ("Confecções" -> "confeccoes")
const normalizar = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const PendenciaCadastro = ({
    empresas, // Lista de empresas
    usuarios, // Lista de usuários
    consultores, // Lista de consultores
    modulos, // Lista de módulos
    initialData, // Dados iniciais para edição
    setCurrentView, // Função de navegação (handleNavigation de App.js)
    currentUser, // Usuário autenticado
    onDeletePendencia, // Função para deletar pendência
    newlyCreatedUser, // Usuário recém-criado vindo do App.js
    clearNewlyCreatedUser, // Função para limpar o newlyCreatedUser em App.js
    souAdmin = false, // só admin vê o atalho para o cadastro de empresas
}) => {
    const [formData, setFormData] = useState({
        empresaId: '', empresa: '',
        moduloId: '', modulo: '',
        usuarioId: '', // Este é o ID Firebase do documento de usuário (o campo que o select espera)
        usuario: '', setor: '', telefone: '',
        descricao: '',
        conclusao: '', // NOVO CAMPO: Conclusão
        status: 'Pendente', prioridade: 5,
        tecnicoId: '', tecnico: '',
        chamado: '',
        timestampCriacao: null, timestampAlteracao: null,
        dtLancamento: '', hora: '',
        nrPendenciaSequencial: null, dtAtualizacao: null, horaAtualizacao: null,
    });
    const [isEditing, setIsEditing] = useState(false);
    const [filteredUsersByCompany, setFilteredUsersByCompany] = useState([]);
    const [empresaSearch, setEmpresaSearch] = useState(''); // Texto digitado no campo de busca de empresa
    const [showEmpresaOptions, setShowEmpresaOptions] = useState(false); // Controla a exibição da lista de sugestões
    const [empresaDestaque, setEmpresaDestaque] = useState(0); // Opção destacada ao navegar com as setas
    const listaEmpresasRef = useRef(null);
    const empresaInputRef = useRef(null); // recebe o foco ao abrir uma pendência nova
    const preenchidoParaRef = useRef(null); // qual pendência já preencheu o formulário
    // Cadastro rápido de usuário, sem sair da pendência
    const NOVO_USUARIO_VAZIO = { aberto: false, nome: '', telefone: '', setor: '', salvando: false, erro: '' };
    const [novoUsuario, setNovoUsuario] = useState(NOVO_USUARIO_VAZIO);
    const [usuarioCriadoAqui, setUsuarioCriadoAqui] = useState(null); // aparece no seletor até a lista atualizar
    const novoUsuarioNomeRef = useRef(null);
    const [statusHistory, setStatusHistory] = useState([]);
    const [loadingFormData, setLoadingFormData] = useState(true); // Inicializado com true corretamente
    const [concluirAoSalvar, setConcluirAoSalvar] = useState(false); // grava e já conclui, sem passar pelo quadro
    const [salvando, setSalvando] = useState(false);
    const [avisoInatividade, setAvisoInatividade] = useState(false); // mostra a opção de concluir depois de enviar
    const formRef = useRef(null);
    const jaConcluida = isEditing && formData.status === 'Concluído';

    // Só os módulos ativos aparecem para escolha. Se a pendência já usa um módulo
    // que foi inativado depois, ele continua na lista para não sumir da tela.
    const modulosParaEscolha = useMemo(
        () => modulos.filter(m => m.ativo !== false || m.idFirebase === formData.moduloId),
        [modulos, formData.moduloId]
    );
    // mesma regra para empresas e técnicos (consultores)
    const empresasParaEscolha = useMemo(
        () => empresas.filter(e => e.ativo !== false || e.idFirebase === formData.empresaId),
        [empresas, formData.empresaId]
    );
    const consultoresParaEscolha = useMemo(
        // inativos e "somente acesso" (ex.: sócio) não aparecem, a não ser o técnico que já está na pendência
        () => consultores.filter(c => (c.ativo !== false && !c.soAcesso) || c.idFirebase === formData.tecnicoId),
        [consultores, formData.tecnicoId]
    );

    // opções dos campos com busca enquanto digita
    const opcoesModulo = useMemo(
        () => modulosParaEscolha.map(m => ({ valor: m.idFirebase, rotulo: m.descricao || '', detalhe: m.ativo === false ? 'inativo' : '' })),
        [modulosParaEscolha]
    );
    const opcoesTecnico = useMemo(
        () => consultoresParaEscolha.map(c => ({ valor: c.idFirebase, rotulo: c.nome || '', detalhe: c.ativo === false ? 'inativo' : '' })),
        [consultoresParaEscolha]
    );

    // celular no formato (11) 91234-5678 para aparecer ao lado do nome na busca de usuário
    const formatarCelular = (tel) => {
        let d = String(tel || '').replace(/\D/g, '');
        if (d.length > 11 && d.startsWith('55')) d = d.slice(2);
        if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
        if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
        return String(tel || '').trim();
    };

    const opcoesUsuario = useMemo(() => {
        const lista = filteredUsersByCompany.map(u => ({
            valor: u.idFirebase, rotulo: u.nome || '', extra: formatarCelular(u.telefone), detalhe: [u.setor, u.ativo === false ? 'inativo' : ''].filter(Boolean).join(' · '),
        }));
        // usuário recém-cadastrado que ainda não chegou na lista
        [newlyCreatedUser, usuarioCriadoAqui].forEach(n => {
            if (n && n.idFirebase && formData.usuarioId === n.idFirebase && !lista.some(o => o.valor === n.idFirebase)) {
                lista.push({ valor: n.idFirebase, rotulo: n.nome || '', extra: formatarCelular(n.telefone), detalhe: n.setor || 'novo' });
            }
        });
        return lista;
    }, [filteredUsersByCompany, newlyCreatedUser, usuarioCriadoAqui, formData.usuarioId]);

    // pendência nova: o cursor já começa na busca de empresa
    useEffect(() => {
        if (!loadingFormData && !initialData) {
            const t = setTimeout(() => empresaInputRef.current && empresaInputRef.current.focus(), 80);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [loadingFormData, initialData]);

    // Efeito para carregar dados iniciais (se for edição) e histórico
    useEffect(() => {
        // Verifica se todos os dados globais essenciais estão carregados
        if (empresas.length === 0 || (initialData && usuarios.length === 0) || modulos.length === 0 || consultores.length === 0) {
            setLoadingFormData(true);
            return;
        }

        setLoadingFormData(false);

        // Preenche o formulário UMA vez por pendência aberta. Antes, qualquer mudança nas listas
        // (por exemplo, cadastrar um usuário novo aqui) reiniciava o formulário e apagava o que foi digitado.
        const chave = initialData ? initialData.id : '__nova__';
        if (preenchidoParaRef.current === chave) return;
        preenchidoParaRef.current = chave;
        setConcluirAoSalvar(false);

        console.log("DEBUG: PendenciaCadastro - useEffect inicial (com dependências globais) disparado. InitialData:", initialData);
        if (initialData) {
            setIsEditing(true);

            // Procura pelo ID gravado; o nome só é usado em pendências antigas sem ID
            // (assim, renomear uma empresa ou módulo não "perde" as pendências dela)
            const empresaFound = empresas.find(emp => emp.idFirebase === initialData.empresaId) ||
                                 empresas.find(emp => (emp.nome || '').toUpperCase() === (initialData.empresa || '').toUpperCase());
            const moduloFound = modulos.find(mod => mod.idFirebase === initialData.moduloId) ||
                                modulos.find(mod => (mod.descricao || '').toUpperCase() === (initialData.modulo || '').toUpperCase());
            const tecnicoFound = consultores.find(tec => tec.idFirebase === initialData.tecnicoId) ||
                                 consultores.find(tec => tec.nome === initialData.tecnico);

            let usuarioIdParaForm = initialData.usuarioId || '';
            let telefoneParaForm = initialData.telefoneUsuario || '';
            let setorParaForm = initialData.setor || '';
            let usuarioNomeParaForm = initialData.usuario || '';

            console.log("DEBUG: InitialData recebido para verificação de usuário:", initialData);
            console.log("DEBUG: usuarioIdParaForm inicial do initialData (DEVERIA SER O ID CORRETO):", usuarioIdParaForm);

            let userFoundInGlobalList = null;

            if (usuarioIdParaForm) {
                userFoundInGlobalList = usuarios.find(user => user.idFirebase === usuarioIdParaForm);
                if (userFoundInGlobalList) {
                    usuarioNomeParaForm = userFoundInGlobalList.nome;
                    telefoneParaForm = userFoundInGlobalList.telefone || '';
                    setorParaForm = userFoundInGlobalList.setor || '';
                    console.log(`DEBUG: Usuário encontrado por ID: ${userFoundInGlobalList.nome} (ID: ${userFoundInGlobalList.idFirebase}) - Tel: ${telefoneParaForm} - Setor: ${setorParaForm}`);
                } else {
                    console.warn(`DEBUG: Usuário com ID ${usuarioIdParaForm} (da pendência) NÃO encontrado na lista de usuários carregada. Tentando fallback por nome...`);
                    telefoneParaForm = initialData.telefoneUsuario || '';
                    setorParaForm = initialData.setor || '';
                }
            }

            if (!userFoundInGlobalList && initialData.usuario && empresaFound?.idFirebase) {
                console.warn("DEBUG: Tentando encontrar usuário por nome e ID da Empresa como fallback.");
                const userByNameAndCompany = usuarios.find(user =>
                    user.nome === initialData.usuario &&
                    user.empresaId === empresaFound.idFirebase
                );
                if (userByNameAndCompany) {
                    usuarioIdParaForm = userByNameAndCompany.idFirebase;
                    usuarioNomeParaForm = userByNameAndCompany.nome;
                    telefoneParaForm = userByNameAndCompany.telefone || '';
                    setorParaForm = userByNameAndCompany.setor || '';
                    console.log(`DEBUG: Usuário encontrado por nome/empresa fallback: ${userByNameAndCompany.nome} (ID: ${userByNameAndCompany.idFirebase}) - Tel: ${telefoneParaForm} - Setor: ${setorParaForm}`);
                } else {
                    console.warn("DEBUG: Nenhum usuário correspondente encontrado por nome/empresa no fallback. Campo Usuário será 'Selecione...'.");
                    telefoneParaForm = initialData.telefoneUsuario || '';
                    setorParaForm = initialData.setor || '';
                }
            }

            const newFormData = {
                empresaId: empresaFound ? empresaFound.idFirebase : '',
                empresa: empresaFound ? empresaFound.nome : (initialData.empresa || ''),
                moduloId: moduloFound ? moduloFound.idFirebase : '',
                modulo: moduloFound ? moduloFound.descricao : (initialData.modulo || ''),
                usuarioId: usuarioIdParaForm,
                usuario: usuarioNomeParaForm,
                setor: setorParaForm,
                telefone: telefoneParaForm,
                descricao: initialData.descricao || '',
                conclusao: initialData.conclusao || '', // Adicionado: preenche o campo conclusao
                status: initialData.status || 'Pendente',
                prioridade: initialData.prioridade || 5,
                tecnicoId: tecnicoFound ? tecnicoFound.idFirebase : '',
                tecnico: initialData.tecnico || '',
                chamado: initialData.chamado || '',
                timestampCriacao: initialData.timestampCriacao || null,
                timestampAlteracao: initialData.timestampAlteracao || null,
                dtLancamento: initialData.dtLancamento || new Date().toLocaleDateString('pt-BR', {timeZone: 'America/Sao_Paulo'}),
                hora: initialData.hora || new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }),
                nrPendenciaSequencial: initialData.nrPendenciaSequencial || null,
                dtAtualizacao: initialData.dtAtualizacao || null,
                horaAtualizacao: initialData.horaAtualizacao || null,
            };

            setTimeout(() => {
                setFormData(newFormData);
                console.log("DEBUG: formData final (APÓS TIMEOUT) - para renderização da UI:", {
                    usuarioId: newFormData.usuarioId,
                    usuario: newFormData.usuario,
                    setor: newFormData.setor,
                    telefone: newFormData.telefone,
                    empresaId: newFormData.empresaId,
                    conclusao: newFormData.conclusao, // Debug do novo campo
                });
            }, 50);


            if (empresaFound?.idFirebase && usuarios.length > 0) {
                // usuários inativos não aparecem, a não ser o que já está na pendência
                const usersForSelectedCompany = usuarios.filter(user => user.empresaId === empresaFound.idFirebase &&
                    (user.ativo !== false || user.idFirebase === usuarioIdParaForm));
                setFilteredUsersByCompany(usersForSelectedCompany);
                console.log("DEBUG: Usuários filtrados DEFINIDOS no useEffect inicial:", usersForSelectedCompany);
            } else {
                setFilteredUsersByCompany([]);
            }

        } else {
            console.log("PendenciaCadastro - Limpando formulário para nova pendência.");
            const now = new Date();
            setFormData(prev => ({
                empresaId: '', empresa: '',
                moduloId: '', modulo: '',
                usuarioId: '', usuario: '', setor: '', telefone: '',
                descricao: '',
                conclusao: '', // Adicionado: limpa o campo conclusao
                status: 'Pendente', prioridade: 5,
                tecnicoId: '', tecnico: '',
                chamado: '',
                timestampCriacao: null, timestampAlteracao: null,
                dtLancamento: now.toLocaleDateString('pt-BR', {timeZone: 'America/Sao_Paulo'}),
                hora: now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }),
                nrPendenciaSequencial: null,
                dtAtualizacao: null,
                horaAtualizacao: null,
            }));
            setIsEditing(false);
            setFilteredUsersByCompany([]);
            setStatusHistory([]);
        }
    }, [initialData, empresas, usuarios, modulos, consultores]);

    // Histórico de movimentações da pendência em edição (separado do preenchimento do formulário)
    const idPendencia = initialData ? initialData.id : null;
    useEffect(() => {
        if (!idPendencia) { setStatusHistory([]); return undefined; }
        let ativo = true;
        api.get(`/pendencias/${idPendencia}/historico`)
            .then((h) => { if (ativo) setStatusHistory(h); })
            .catch((error) => console.error("Erro ao carregar historico de status:", error));
        return () => { ativo = false; };
    }, [idPendencia]);

    useEffect(() => {
        console.log("DEBUG: PendenciaCadastro - useEffect de usuários filtrados disparado (no segundo useEffect).");
        if (formData.empresaId && usuarios.length > 0) {
            const usersForSelectedCompany = usuarios.filter(user => user.empresaId === formData.empresaId &&
                (user.ativo !== false || user.idFirebase === formData.usuarioId));
            setFilteredUsersByCompany(usersForSelectedCompany);
            console.log("DEBUG: Usuários filtrados pela empresa (no segundo useEffect):", usersForSelectedCompany);

            const isCurrentUserInFilteredList = usersForSelectedCompany.some(user => user.idFirebase === formData.usuarioId);

            const acabouDeCriar = usuarioCriadoAqui && usuarioCriadoAqui.idFirebase === formData.usuarioId;
            if (!isEditing && formData.usuarioId && !isCurrentUserInFilteredList && !acabouDeCriar) {
                console.log("DEBUG: Usuário selecionado não pertence mais à empresa selecionada ou não existe. Resetando.");
                setFormData(prev => ({ ...prev, usuarioId: '', usuario: '', setor: '', telefone: '' }));
            }
        } else {
            setFilteredUsersByCompany([]);
            if (!isEditing) {
                 setFormData(prev => ({ ...prev, usuarioId: '', usuario: '', setor: '', telefone: '' }));
            }
        }
    }, [formData.empresaId, usuarios, formData.usuarioId, isEditing, usuarioCriadoAqui]);

    useEffect(() => {
        if (newlyCreatedUser && newlyCreatedUser.idFirebase) {
            console.log("DEBUG: PendenciaCadastro - Preenchendo com newlyCreatedUser:", newlyCreatedUser);
            setFormData(prev => ({
                ...prev,
                usuarioId: newlyCreatedUser.idFirebase,
                usuario: newlyCreatedUser.nome,
                setor: newlyCreatedUser.setor,
                telefone: newlyCreatedUser.telefone,
                empresaId: newlyCreatedUser.empresaId || prev.empresaId,
                empresa: empresas.find(emp => emp.idFirebase === (newlyCreatedUser.empresaId || prev.empresaId))?.nome || prev.empresa,
            }));
            clearNewlyCreatedUser();
        } else if (newlyCreatedUser) {
            console.warn("DEBUG: newlyCreatedUser recebido, mas sem idFirebase:", newlyCreatedUser);
            clearNewlyCreatedUser();
        }
    }, [newlyCreatedUser, clearNewlyCreatedUser, empresas]);


    // Lista de empresas filtrada enquanto digita (sem diferenciar maiúsculas nem acentos).
    // As que COMEÇAM com o texto aparecem primeiro, depois as que apenas contêm o texto.
    const empresasFiltradas = useMemo(() => {
        const termo = normalizar(empresaSearch);
        if (!termo) return empresasParaEscolha;
        const comeca = [];
        const contem = [];
        empresasParaEscolha.forEach(emp => {
            const nome = normalizar(emp.nome);
            if (nome.startsWith(termo)) comeca.push(emp);
            else if (nome.includes(termo)) contem.push(emp);
        });
        return [...comeca, ...contem];
    }, [empresasParaEscolha, empresaSearch]);

    // Volta o destaque para a primeira opção sempre que a lista muda
    useEffect(() => { setEmpresaDestaque(0); }, [empresaSearch]);

    // Mantém a opção destacada visível ao navegar com as setas
    useEffect(() => {
        const el = listaEmpresasRef.current && listaEmpresasRef.current.children[empresaDestaque];
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }, [empresaDestaque]);

    // Preenche o campo com o nome da empresa quando ela é escolhida por fora (edição, novo usuário).
    // Antes isso dependia de formData.empresa e apagava o texto a cada letra digitada.
    useEffect(() => {
        if (formData.empresaId) setEmpresaSearch(formData.empresa || '');
    }, [formData.empresaId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Teclado no campo de empresa: ↑ ↓ navegam, Enter escolhe, Esc fecha
    const handleEmpresaKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setShowEmpresaOptions(true);
            setEmpresaDestaque(i => Math.min(i + 1, Math.max(0, empresasFiltradas.length - 1)));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setEmpresaDestaque(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            if (showEmpresaOptions && empresasFiltradas[empresaDestaque]) {
                e.preventDefault(); // não envia o formulário
                handleSelectEmpresa(empresasFiltradas[empresaDestaque]);
            }
        } else if (e.key === 'Escape') {
            setShowEmpresaOptions(false);
        }
    };

    // Ao sair do campo sem escolher: se o texto bate exatamente com uma empresa, seleciona ela;
    // se não bate e nenhuma empresa está selecionada, limpa o texto
    const handleEmpresaBlur = () => {
        setTimeout(() => {
            setShowEmpresaOptions(false);
            if (formData.empresaId) return;
            const exata = empresasParaEscolha.find(emp => normalizar(emp.nome) === normalizar(empresaSearch));
            if (exata) handleSelectEmpresa(exata);
        }, 150);
    };

    // Destaca no nome a parte que bate com o texto digitado
    const destacarTermo = (nome) => {
        const termo = normalizar(empresaSearch);
        const i = termo ? normalizar(nome).indexOf(termo) : -1;
        if (i < 0) return nome;
        return (
            <>
                {nome.slice(0, i)}
                <b className="text-indigo-700">{nome.slice(i, i + termo.length)}</b>
                {nome.slice(i + termo.length)}
            </>
        );
    };

    // Chamado ao digitar no campo de busca de empresa
    const handleEmpresaSearchChange = (e) => {
        setEmpresaSearch(e.target.value.toUpperCase());
        setShowEmpresaOptions(true);
        // Enquanto o usuário digita e não confirma uma opção, limpa a empresa selecionada
        if (formData.empresaId) {
            setFormData(prev => ({
                ...prev,
                empresaId: '',
                empresa: '',
                usuarioId: '',
                usuario: '',
                setor: '',
                telefone: ''
            }));
        }
    };

    // Chamado ao clicar em uma empresa da lista de sugestões
    const handleSelectEmpresa = (emp) => {
        setFormData(prev => ({
            ...prev,
            empresaId: emp.idFirebase,
            empresa: emp.nome,
            usuarioId: '',
            usuario: '',
            setor: '',
            telefone: ''
        }));
        setEmpresaSearch(emp.nome);
        setShowEmpresaOptions(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === 'chamado' || name === 'descricao' || name === 'conclusao') {
            newValue = value.toUpperCase();
            // Ao trocar o texto por maiúsculas, o React joga o cursor para o FIM do campo
            // (que pode ser a linha de baixo). Guarda a posição e devolve o cursor onde estava.
            const el = e.target;
            if (newValue !== value && typeof el.selectionStart === 'number') {
                const ini = el.selectionStart;
                const fim = el.selectionEnd;
                requestAnimationFrame(() => {
                    try { el.setSelectionRange(ini, fim); } catch (_) { /* campo saiu da tela */ }
                });
            }
        }

        setFormData(prev => ({ ...prev, [name]: newValue }));

        if (name === 'empresaId') {
            const selectedEmpresa = empresas.find(emp => emp.idFirebase === newValue);
            setFormData(prev => ({
                ...prev,
                empresa: selectedEmpresa ? selectedEmpresa.nome : '',
                usuarioId: '',
                usuario: '',
                setor: '',
                telefone: ''
            }));
        } else if (name === 'moduloId') {
            const selectedModulo = modulos.find(mod => mod.idFirebase === newValue);
            setFormData(prev => ({
                ...prev,
                modulo: selectedModulo ? selectedModulo.descricao : '',
            }));
        } else if (name === 'tecnicoId') {
            const selectedTecnico = consultores.find(tec => tec.idFirebase === newValue);
            setFormData(prev => ({
                ...prev,
                tecnico: selectedTecnico ? selectedTecnico.nome : '',
            }));
        } else if (name === 'usuarioId') {
            const selectedUser = filteredUsersByCompany.find(user => user.idFirebase === newValue);
            setFormData(prev => ({
                ...prev,
                usuario: selectedUser ? selectedUser.nome : '',
                setor: selectedUser ? selectedUser.setor : '',
                telefone: selectedUser ? selectedUser.telefone : '',
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.empresaId || !formData.moduloId || !formData.usuarioId || !formData.descricao || formData.prioridade === '' || !formData.tecnicoId || !formData.telefone || !formData.setor) {
            alert("Por favor, preencha todos os campos obrigatórios.");
            return;
        }
        const concluir = concluirAoSalvar && !jaConcluida;
        if (concluir && !String(formData.conclusao || '').trim()) {
            alert("Para concluir, descreva a ação tomada no campo Conclusão.");
            return;
        }
        if (salvando) return;
        setSalvando(true);

        try {
            const finalEmpresaNome = empresas.find(emp => emp.idFirebase === formData.empresaId)?.nome || formData.empresa;
            const finalModuloDesc = modulos.find(mod => mod.idFirebase === formData.moduloId)?.descricao || formData.modulo;
            const finalTecnicoNome = consultores.find(tec => tec.idFirebase === formData.tecnicoId)?.nome || formData.tecnico;

            const finalUserObject = usuarios.find(user => user.idFirebase === formData.usuarioId);
            const finalUsuarioNome = finalUserObject ? finalUserObject.nome : formData.usuario;
            const finalSetor = finalUserObject ? finalUserObject.setor : formData.setor;
            const finalTelefone = finalUserObject ? finalUserObject.telefone : formData.telefone;

            const dataToSave = {
                empresaId: formData.empresaId,
                empresa: String(finalEmpresaNome || '').toUpperCase(),
                moduloId: formData.moduloId,
                modulo: String(finalModuloDesc || '').toUpperCase(),
                usuarioId: formData.usuarioId,
                usuario: String(finalUsuarioNome || '').toUpperCase(),
                setor: String(finalSetor || '').toUpperCase(),
                telefoneUsuario: String(finalTelefone || '').toUpperCase(),
                descricao: String(formData.descricao || '').toUpperCase(),
                conclusao: String(formData.conclusao || '').toUpperCase(), // Adicionado: salva o campo conclusao
                status: formData.status,
                prioridade: parseInt(formData.prioridade, 10),
                tecnicoId: formData.tecnicoId,
                tecnico: String(finalTecnicoNome || '').toUpperCase(),
                chamado: String(formData.chamado || '').toUpperCase(),
            };

            let id;
            let nr;
            if (isEditing) {
                await api.put(`/pendencias/${initialData.id}`, dataToSave);
                id = initialData.id;
                nr = initialData.nrPendenciaSequencial;
            } else {
                const criada = await api.post('/pendencias', dataToSave);
                id = criada.id;
                nr = criada.nrPendenciaSequencial;
            }

            if (concluir) {
                // mesma rota do botão "Concluir" do quadro: grava status, data de conclusão e histórico
                try {
                    await api.post(`/pendencias/${id}/status`, { status: 'Concluído' });
                } catch (erroConcluir) {
                    recarregar('pendencias');
                    alert(`A pendência${nr ? ` nº ${nr}` : ''} foi salva, mas não foi possível concluí-la: ${erroConcluir.message || erroConcluir}\nConclua pelo quadro de pendências.`);
                    setCurrentView('pendencias');
                    return;
                }
            }

            recarregar('pendencias');
            if (concluir) {
                alert(`Pendência${nr ? ` nº ${nr}` : ''} salva e concluída!`);
            } else if (isEditing) {
                alert("Pendência atualizada com sucesso!");
            } else {
                alert(`Pendência nº ${nr} cadastrada com sucesso!`);
            }
            setCurrentView('pendencias');
        } catch (error) {
            console.error("Erro ao salvar pendência:", error);
            alert(`Erro ao salvar a pendência: ${error.message || error}`);
        } finally {
            setSalvando(false);
        }
    };

    const handleDelete = useCallback(() => {
        if (window.confirm("Tem certeza que deseja excluir esta pendência?")) {
            onDeletePendencia(initialData.id);
            setCurrentView('pendencias');
        }
    }, [initialData, onDeletePendencia, setCurrentView]);

    const handleNew = useCallback(() => {
        setFormData({
            empresaId: '', empresa: '', moduloId: '', modulo: '', usuarioId: '', usuario: '', setor: '', telefone: '',
            descricao: '',
            conclusao: '', // Adicionado: limpa o campo conclusao
            status: 'Pendente', prioridade: 5, tecnicoId: '', tecnico: '', chamado: '',
            timestampCriacao: null, timestampAlteracao: null,
            dtLancamento: new Date().toLocaleDateString('pt-BR', {timeZone: 'America/Sao_Paulo'}),
            hora: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }),
            nrPendenciaSequencial: null,
            dtAtualizacao: null,
            horaAtualizacao: null,
        });
        setIsEditing(false);
        setFilteredUsersByCompany([]);
        setStatusHistory([]);
        setNovoUsuario({ aberto: false, nome: '', telefone: '', setor: '', salvando: false, erro: '' });
        setUsuarioCriadoAqui(null);
        setConcluirAoSalvar(false);
        clearNewlyCreatedUser();
        setEmpresaSearch('');
        setTimeout(() => empresaInputRef.current && empresaInputRef.current.focus(), 80);
    }, [clearNewlyCreatedUser]);

    // "+ Usuário": abre os campos de novo usuário aqui mesmo, sem perder o que já foi digitado
    const abrirNovoUsuario = () => {
        if (!formData.empresaId) {
            alert('Por favor, selecione uma empresa antes de cadastrar um novo usuário.');
            return;
        }
        setNovoUsuario(prev => (prev.aberto ? NOVO_USUARIO_VAZIO : { ...NOVO_USUARIO_VAZIO, aberto: true }));
        setTimeout(() => novoUsuarioNomeRef.current && novoUsuarioNomeRef.current.focus(), 50);
    };

    const alterarNovoUsuario = (campo) => (e) => {
        const valor = e.target.value.toUpperCase();
        setNovoUsuario(prev => ({ ...prev, [campo]: valor, erro: '' }));
    };

    // Grava o usuário na empresa selecionada e já o seleciona na pendência
    const salvarNovoUsuario = async () => {
        const nome = novoUsuario.nome.trim();
        const telefone = novoUsuario.telefone.trim();
        const setor = novoUsuario.setor.trim();
        if (!nome || !telefone) {
            setNovoUsuario(prev => ({ ...prev, erro: 'Preencha o nome e o telefone do usuário.' }));
            return;
        }
        if (!formData.empresaId) {
            setNovoUsuario(prev => ({ ...prev, erro: 'Selecione a empresa primeiro.' }));
            return;
        }
        const jaExiste = usuarios.find(u => u.empresaId === formData.empresaId && normalizar(u.nome) === normalizar(nome));
        if (jaExiste) {
            setNovoUsuario(prev => ({ ...prev, erro: jaExiste.ativo === false
                ? `Já existe o usuário ${jaExiste.nome} nesta empresa, mas está inativo. Reative-o no cadastro da empresa.`
                : `Já existe o usuário ${jaExiste.nome} nesta empresa. Selecione-o na lista.` }));
            return;
        }

        setNovoUsuario(prev => ({ ...prev, salvando: true, erro: '' }));
        try {
            const r = await api.post('/usuarios', { nome, telefone, setor, empresaId: formData.empresaId });
            recarregar('usuarios');
            const ref = { id: r.idFirebase };
            const criado = { idFirebase: r.idFirebase, nome: r.nome, telefone: r.telefone, setor: r.setor, empresaId: formData.empresaId };
            setUsuarioCriadoAqui(criado);
            setFormData(prev => ({ ...prev, usuarioId: ref.id, usuario: nome, setor, telefone }));
            setNovoUsuario(NOVO_USUARIO_VAZIO);
        } catch (err) {
            console.error('Erro ao cadastrar usuário:', err);
            setNovoUsuario(prev => ({ ...prev, salvando: false, erro: `Erro ao salvar o usuário: ${err.message}` }));
        }
    };

    // Enter nos campos do novo usuário salva o usuário (e não a pendência); Esc cancela
    const teclaNovoUsuario = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); salvarNovoUsuario(); }
        if (e.key === 'Escape') setNovoUsuario(NOVO_USUARIO_VAZIO);
    };

    // Nova função para enviar mensagem WhatsApp
    const handleEnviarWhatsApp = useCallback(() => {
        // Validações básicas
        if (!formData.telefone) {
            alert("O telefone do usuário não está disponível.");
            return;
        }
        if (!formData.tecnico) {
            alert("O nome do técnico não está disponível.");
            return;
        }
        if (!initialData?.nrPendenciaSequencial) {
            alert("O número da pendência não está disponível.");
            return;
        }

        // Formata o número de telefone (remove não-dígitos para garantir formato limpo)
        const numeroLimpo = formData.telefone.replace(/\D/g, ''); // Remove tudo que não for dígito
        // Adiciona o código do país e DDD se não estiver presente (ex: +55 para Brasil)
        // Você pode precisar adaptar isso para sua realidade de números
        const numeroFormatado = numeroLimpo.startsWith('55') ? numeroLimpo : `55${numeroLimpo}`; 

        const mensagem = `Olá, tudo bem? Meu nome é ${formData.tecnico}, sou consultor da Sisplan. Existe um chamado (Nr: ${initialData.nrPendenciaSequencial}) em aberto. Como posso ajudar?`;
        
        abrirWhatsApp(numeroFormatado, mensagem);
    }, [formData.telefone, formData.tecnico, initialData?.nrPendenciaSequencial]);

    // WhatsApp de inatividade: avisa o usuário (mesmo telefone do card) que o chamado será encerrado
    const MENSAGEM_INATIVIDADE = 'Seu chamado está sendo encerrado por inatividade. Entre em contato para gerar um novo chamado e entrar na fila de atendimentos.';
    const handleWhatsAppInatividade = () => {
        const numeroLimpo = String(formData.telefone || '').replace(/\D/g, '');
        if (!numeroLimpo) {
            alert('O telefone do usuário não está disponível.');
            return;
        }
        const numero = numeroLimpo.startsWith('55') ? numeroLimpo : `55${numeroLimpo}`;
        abrirWhatsApp(numero, MENSAGEM_INATIVIDADE);
        if (!jaConcluida) setAvisoInatividade(true);
    };

    // depois de avisar: preenche a ação tomada e conclui (mesmo caminho do "Salvar e concluir")
    const concluirPorInatividade = () => {
        const agora = new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        const texto = `ENCERRADO POR INATIVIDADE. USUÁRIO AVISADO PELO WHATSAPP EM ${agora}.`;
        setFormData(prev => ({ ...prev, conclusao: prev.conclusao && prev.conclusao.trim() ? `${prev.conclusao.trim()}\n${texto}` : texto }));
        setConcluirAoSalvar(true);
        setAvisoInatividade(false);
        // envia o formulário depois que a tela atualizar com a ação tomada
        setTimeout(() => formRef.current && formRef.current.requestSubmit(), 60);
    };


    return (
        <div className="container mx-auto p-4 bg-white shadow-md rounded-lg">
            <h2 className="text-2xl font-semibold mb-6 text-gray-800">
                Cadastro de Pendência {initialData?.nrPendenciaSequencial ? `(Nr: ${initialData.nrPendenciaSequencial})` : ''}

            </h2>
            {loadingFormData ? (
                <div className="text-center text-gray-600 py-8">Carregando dados para o formulário...</div>
            ) : (
                // Alterado para grid-cols-1 para telas muito pequenas (celular), e sm:grid-cols-2 para telas um pouco maiores (sm e acima)
                <form ref={formRef} onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4"> 
                    {/* Linha 1: Empresa e Prioridade */}
                    {/* sm:grid-cols-2 para que fiquem lado a lado em telas maiores que 'sm' */}
                    <div className="flex flex-col">
                        <label htmlFor="empresaId" className="block text-sm font-medium text-gray-700">Empresa</label>
                        <div className="flex items-center space-x-2 mt-1 relative">
                            <div className="relative w-full">
                                <input
                                    type="text"
                                    id="empresaId"
                                    ref={empresaInputRef}
                                    name="empresaId"
                                    autoComplete="off"
                                    value={empresaSearch}
                                    onChange={handleEmpresaSearchChange}
                                    onFocus={(e) => { setShowEmpresaOptions(true); e.target.select(); }}
                                    onBlur={handleEmpresaBlur}
                                    onKeyDown={handleEmpresaKeyDown}
                                    placeholder="Digite para buscar a empresa..."
                                    className={`block w-full border rounded-md shadow-sm py-2 pl-3 pr-8 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm uppercase ${formData.empresaId ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}
                                    required
                                />
                                {/* botão para limpar a busca */}
                                {empresaSearch && (
                                    <button
                                        type="button"
                                        tabIndex={-1}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setEmpresaSearch('');
                                            setShowEmpresaOptions(true);
                                            setFormData(prev => ({ ...prev, empresaId: '', empresa: '', usuarioId: '', usuario: '', setor: '', telefone: '' }));
                                        }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-lg leading-none"
                                        title="Limpar"
                                    >
                                        ×
                                    </button>
                                )}
                                {showEmpresaOptions && (
                                    <ul ref={listaEmpresasRef} className="absolute z-20 w-full bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto text-sm">
                                        {empresasFiltradas.length > 0 ? (
                                            empresasFiltradas.map((emp, i) => (
                                                <li
                                                    key={emp.idFirebase}
                                                    onMouseDown={(e) => { e.preventDefault(); handleSelectEmpresa(emp); }}
                                                    onMouseEnter={() => setEmpresaDestaque(i)}
                                                    className={`px-3 py-2 cursor-pointer ${i === empresaDestaque ? 'bg-indigo-100' : ''} ${emp.idFirebase === formData.empresaId ? 'font-semibold' : ''}`}
                                                >
                                                    {destacarTermo(emp.nome)}{emp.ativo === false && <span className="ml-1.5 text-[10px] font-semibold uppercase text-gray-500">(inativa)</span>}
                                                </li>
                                            ))
                                        ) : (
                                            <li className="px-3 py-2 text-gray-400">Nenhuma empresa encontrada</li>
                                        )}
                                    </ul>
                                )}
                            </div>
                            {souAdmin && (
                                <button
                                    type="button"
                                    onClick={() => setCurrentView('empresas')}
                                    className="px-2 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-xs whitespace-nowrap"
                                >
                                    + Emp
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <label htmlFor="prioridade" className="block text-sm font-medium text-gray-700">Prioridade</label>
                        <select
                            id="prioridade"
                            name="prioridade"
                            value={formData.prioridade}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            required
                        >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                    </div>

                    {/* Linha 2: Modulo e Tecnico (busca enquanto digita) */}
                    <div className="flex flex-col">
                        <label htmlFor="moduloId" className="block text-sm font-medium text-gray-700">Módulo</label>
                        <BuscaSelect
                            id="moduloId"
                            className="mt-1"
                            placeholder="Digite para buscar o módulo..."
                            vazio="Nenhum módulo encontrado"
                            valor={formData.moduloId}
                            onChange={(v) => handleChange({ target: { name: 'moduloId', value: v } })}
                            opcoes={opcoesModulo}
                        />
                    </div>
                    <div className="flex flex-col">
                        <label htmlFor="tecnicoId" className="block text-sm font-medium text-gray-700">Técnico</label>
                        <BuscaSelect
                            id="tecnicoId"
                            className="mt-1"
                            placeholder="Digite para buscar o técnico..."
                            vazio="Nenhum técnico encontrado"
                            valor={formData.tecnicoId}
                            onChange={(v) => handleChange({ target: { name: 'tecnicoId', value: v } })}
                            opcoes={opcoesTecnico}
                        />
                    </div>

                    {/* Linha 3: Usuário e Telefone */}
                    <div className="flex flex-col">
                        <label htmlFor="usuarioId" className="block text-sm font-medium text-gray-700">Usuário</label>
                        <div className="flex items-center space-x-2 mt-1">
                            <BuscaSelect
                                id="usuarioId"
                                className="flex-1 min-w-0"
                                placeholder={formData.empresaId ? 'Digite para buscar o usuário...' : 'Escolha a empresa primeiro'}
                                vazio="Nenhum usuário encontrado nesta empresa"
                                disabled={!formData.empresaId}
                                valor={formData.usuarioId}
                                onChange={(v) => handleChange({ target: { name: 'usuarioId', value: v } })}
                                opcoes={opcoesUsuario}
                            />
                            <button
                                type="button"
                                onClick={abrirNovoUsuario}
                                className={`px-2 py-2 text-white rounded-md text-xs whitespace-nowrap ${novoUsuario.aberto ? 'bg-gray-500 hover:bg-gray-600' : 'bg-green-500 hover:bg-green-600'}`}
                            >
                                {novoUsuario.aberto ? 'Fechar' : '+ Usuário'}
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <label htmlFor="telefone" className="block text-sm font-medium text-gray-700">Telefone</label>
                        <input
                            type="text"
                            id="telefone"
                            name="telefone"
                            value={formData.telefone}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-100 text-sm"
                            readOnly={!!formData.usuarioId} // Torna o campo somente leitura se um usuário for selecionado
                            required
                        />
                    </div>

                    {/* Cadastro rápido de usuário, na própria tela */}
                    {novoUsuario.aberto && (
                        <div className="col-span-full border border-green-300 bg-green-50 rounded-md p-4">
                            <div className="text-sm font-semibold text-green-800 mb-3">
                                Novo usuário para <span className="uppercase">{formData.empresa || 'a empresa selecionada'}</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <label htmlFor="novoUsuarioNome" className="block text-sm font-medium text-gray-700">Nome *</label>
                                    <input
                                        ref={novoUsuarioNomeRef}
                                        id="novoUsuarioNome"
                                        type="text"
                                        value={novoUsuario.nome}
                                        onChange={alterarNovoUsuario('nome')}
                                        onKeyDown={teclaNovoUsuario}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm uppercase focus:outline-none focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="novoUsuarioTelefone" className="block text-sm font-medium text-gray-700">Telefone *</label>
                                    <input
                                        id="novoUsuarioTelefone"
                                        type="tel"
                                        value={novoUsuario.telefone}
                                        onChange={alterarNovoUsuario('telefone')}
                                        onKeyDown={teclaNovoUsuario}
                                        placeholder="47 99999-9999"
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="novoUsuarioSetor" className="block text-sm font-medium text-gray-700">Setor</label>
                                    <input
                                        id="novoUsuarioSetor"
                                        type="text"
                                        value={novoUsuario.setor}
                                        onChange={alterarNovoUsuario('setor')}
                                        onKeyDown={teclaNovoUsuario}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm uppercase focus:outline-none focus:ring-green-500 focus:border-green-500"
                                    />
                                </div>
                            </div>
                            {novoUsuario.erro && <p className="text-red-600 text-sm mt-2">{novoUsuario.erro}</p>}
                            <div className="flex gap-2 mt-3">
                                <button
                                    type="button"
                                    onClick={salvarNovoUsuario}
                                    disabled={novoUsuario.salvando}
                                    className="px-4 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm disabled:opacity-60"
                                >
                                    {novoUsuario.salvando ? 'Salvando...' : 'Salvar usuário'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNovoUsuario(NOVO_USUARIO_VAZIO)}
                                    disabled={novoUsuario.salvando}
                                    className="px-4 py-1.5 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 text-sm"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Linha 4: Nr. Chamado Externo (sempre ocupa 2 colunas) */}
                    <div className="col-span-full flex flex-col"> {/* col-span-full para ocupar toda a largura */}
                        <label htmlFor="chamado" className="block text-sm font-medium text-gray-700">Nr. Chamado Externo</label>
                        <input
                            type="text"
                            id="chamado"
                            name="chamado"
                            value={formData.chamado}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        />
                    </div>

                    {/* Linha 5: Tarefa (Descrição) e Conclusão (Lado a Lado - sempre 2 colunas) */}
                    {/* col-span-full para garantir que este grid de 2 colunas ocupe toda a largura disponível */}
                    <div className="col-span-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col">
                            <label htmlFor="descricao" className="block text-sm font-medium text-gray-700">Tarefa (Descrição)</label>
                            <textarea
                                id="descricao"
                                name="descricao"
                                value={formData.descricao}
                                onChange={handleChange}
                                rows="4"
                                placeholder="DESCREVA A PENDÊNCIA AQUI..."
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                required
                            ></textarea>
                        </div>
                        <div className="flex flex-col">
                            <label htmlFor="conclusao" className="block text-sm font-medium text-gray-700">Conclusão (Ação Tomada)</label>
                            <textarea
                                id="conclusao"
                                name="conclusao"
                                value={formData.conclusao}
                                onChange={handleChange}
                                rows="4"
                                placeholder="DESCREVA A AÇÃO TOMADA PARA RESOLVER A PENDÊNCIA..."
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            ></textarea>
                            {jaConcluida ? (
                                <p className="mt-2 text-sm text-green-700 font-medium">✓ Esta pendência já está concluída.</p>
                            ) : (
                                <label className={`mt-2 flex items-center gap-2 text-sm cursor-pointer select-none rounded-md border px-3 py-2 ${concluirAoSalvar ? 'border-green-500 bg-green-50 text-green-800 font-medium' : 'border-gray-200 text-gray-700'}`}>
                                    <input
                                        type="checkbox"
                                        checked={concluirAoSalvar}
                                        onChange={(e) => setConcluirAoSalvar(e.target.checked)}
                                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                    />
                                    Concluir a pendência ao salvar
                                </label>
                            )}
                        </div>
                    </div>

                    {/* depois do WhatsApp de inatividade: oferece concluir na hora */}
                    {avisoInatividade && !jaConcluida && (
                        <div className="col-span-full flex flex-col sm:flex-row sm:items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
                            <div className="flex-1 text-sm text-amber-900">
                                <span className="font-semibold">Mensagem de inatividade aberta no WhatsApp.</span> Depois de enviar, quer concluir esta pendência por inatividade?
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={concluirPorInatividade} disabled={salvando}
                                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-60">
                                    Concluir por inatividade
                                </button>
                                <button type="button" onClick={() => setAvisoInatividade(false)}
                                    className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md text-sm">
                                    Agora não
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Histórico de Movimentações */}
                    {isEditing && statusHistory.length > 0 && (
                        <div className="col-span-full mt-6"> {/* col-span-full para ocupar toda a largura */}
                            <h3 className="text-xl font-semibold mb-3 text-gray-800">Histórico de Movimentações</h3>
                            <div className="bg-gray-50 p-4 rounded-md shadow-inner max-h-60 overflow-y-auto text-sm">
                                {statusHistory.map((entry, index) => (
                                    <div key={entry.id || index} className="mb-2 p-2 border-b border-gray-200 last:border-b-0">
                                        <p className="text-sm text-gray-700">
                                            <span className="font-medium">De:</span> {entry.oldStatus} <span className="font-medium">Para:</span> {entry.newStatus}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            Por: {entry.userEmail} em {entry.timestamp?.toDate ? entry.timestamp.toDate().toLocaleString('pt-BR') : 'Data Indisponível'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Botoes de Acao (sempre ocupam a largura total e são flexíveis) */}
                    <div className="col-span-full flex flex-wrap justify-start gap-2 mt-6"> {/* col-span-full e flex-wrap para responsividade */}
                        <button
                            type="button"
                            onClick={handleNew}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex-grow sm:flex-none"
                        >
                            Novo
                        </button>
                        <button
                            type="submit"
                            disabled={salvando}
                            className={`px-3 py-1.5 text-white rounded-md text-sm flex-grow sm:flex-none disabled:opacity-60 ${concluirAoSalvar && !jaConcluida ? 'bg-green-600 hover:bg-green-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                        >
                            {salvando ? 'Salvando...' : (concluirAoSalvar && !jaConcluida ? 'Salvar e concluir' : 'Salvar')}
                        </button>
                        <button
                            type="button"
                            onClick={() => setCurrentView('pendencias')}
                            className="px-3 py-1.5 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm flex-grow sm:flex-none"
                        >
                            Cancelar
                        </button>
                        {isEditing && (
                            <button
                                type="button"
                                onClick={handleDelete}
                                className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm flex-grow sm:flex-none"
                            >
                                Excluir
                            </button>
                        )}
                        {/* BOTÃO "ENVIAR WHATSAPP" ADICIONADO AQUI */}
                        {isEditing && ( // Mostra o botão apenas se estiver em modo de edição
                            <button
                                type="button"
                                onClick={handleEnviarWhatsApp}
                                className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm flex-grow sm:flex-none sm:ml-auto" // ml-auto para alinhar à direita em sm e acima
                            >
                                Enviar WhatsApp
                            </button>
                        )}
                        {isEditing && (
                            <button
                                type="button"
                                onClick={handleWhatsAppInatividade}
                                title="Envia pelo WhatsApp: seu chamado está sendo encerrado por inatividade..."
                                className="px-3 py-1.5 bg-amber-500 text-white rounded-md hover:bg-amber-600 text-sm flex-grow sm:flex-none"
                            >
                                WhatsApp inatividade
                            </button>
                        )}
                    </div>
                </form>
            )}
        </div>
    );
};

export default PendenciaCadastro;